import React from 'react';

import { getOverallMentorSatisfaction } from '../../../../api/v2/program_insights';
import useProgramInsightsMetric from '../use_program_insights_metric';
import MetricCard from '../metric_card';
import KpiCards from '../kpi_cards';
import styles from './index.module.css';

const DESCRIPTION =
  'Participant-rated mentor satisfaction (1–5 stars) across the overall program and by engagement ' +
  'stage, collected via post-conversation surveys, with a rating distribution and by-country averages.';

// Five star glyphs, filled up to the rounded average.
function Stars({ average }) {
  const filled = average == null ? 0 : Math.round(average);

  return (
    <span className={styles.stars} aria-hidden='true'>
      {[1, 2, 3, 4, 5].map((position) => (
        <span key={position} className={position <= filled ? styles.starFilled : styles.starEmpty}>
          ★
        </span>
      ))}
    </span>
  );
}

function ScoreCard({ label, average }) {
  return (
    <div className={styles.scoreCard}>
      <div className={styles.scoreLabel}>{label}</div>
      <div className={styles.scoreValue}>{average == null ? '—' : average.toFixed(1)}</div>
      <Stars average={average} />
    </div>
  );
}

function Bar({ label, value, fraction }) {
  return (
    <li className={styles.bar}>
      <span className={styles.barLabel}>{label}</span>
      <div className={styles.track}>
        <div className={styles.fill} style={{ width: `${fraction * 100}%` }} />
      </div>
      <span className={styles.barValue}>{value}</span>
    </li>
  );
}

export default function OverallMentorSatisfaction({ filters }) {
  const { data, status } = useProgramInsightsMetric(getOverallMentorSatisfaction, filters);

  const hasData = data && data.overall.submitted_ratings > 0;
  const maxRating = hasData ? Math.max(...data.rating_distribution.map((bucket) => bucket.count), 1) : 1;
  const ratingsDescending = hasData ? [...data.rating_distribution].sort((a, b) => b.rating - a.rating) : [];

  return (
    <MetricCard
      title='Overall Mentor Satisfaction Score'
      subtitle='Survey submitted before or after study consent. One participant can be in both.'
      description={DESCRIPTION}
    >
      {status === 'loading' && <p className={styles.state}>Loading…</p>}
      {status === 'error' && <p className={styles.stateError}>We could not load this metric. Try again shortly.</p>}

      {status === 'loaded' && !hasData && (
        <p className={styles.state}>No mentor ratings match the current filters yet.</p>
      )}

      {status === 'loaded' && hasData && (
        <>
          <div className={styles.scoreCards}>
            <ScoreCard label='Program' average={data.overall.average} />
            {data.recruitment_and_retention && <ScoreCard label='Pre-consent' average={data.pre_consent.average} />}
            {data.recruitment_and_retention && <ScoreCard label='Post-consent' average={data.post_consent.average} />}
          </div>

          <KpiCards
            cards={[
              { label: 'Submitted Ratings', value: data.overall.submitted_ratings.toLocaleString() },
              { label: 'Response Rate', value: `${data.overall.response_rate}%` },
            ]}
          />

          <div className={styles.columns}>
            <section className={styles.column}>
              <h3 className={styles.sectionLabel}>Rating distribution</h3>
              <ul className={styles.bars}>
                {ratingsDescending.map((bucket) => (
                  <Bar
                    key={bucket.rating}
                    label={`${bucket.rating} ★`}
                    value={bucket.count.toLocaleString()}
                    fraction={bucket.count / maxRating}
                  />
                ))}
              </ul>
            </section>

            <section className={styles.column}>
              <h3 className={styles.sectionLabel}>Average score by country</h3>
              {data.by_country.length > 0 ? (
                <ul className={styles.bars}>
                  {data.by_country.map((row) => (
                    <Bar
                      key={row.country || 'unknown'}
                      label={row.country || 'Unspecified'}
                      value={
                        row.average == null
                          ? '—'
                          : `${row.average.toFixed(1)} ★ · ${row.count.toLocaleString()} ${row.count === 1 ? 'rating' : 'ratings'}`
                      }
                      fraction={(row.average || 0) / 5}
                    />
                  ))}
                </ul>
              ) : (
                <p className={styles.state}>No country is recorded for these ratings.</p>
              )}
            </section>
          </div>
        </>
      )}
    </MetricCard>
  );
}
